import { useState, useCallback } from 'react';

/**
 * Custom hook for managing loading, error, and data states
 * Provides a consistent API across components for async operations
 * 
 * @param {boolean} initialLoading - Initial loading state (default: false)
 * @returns {Object} Loading state management object
 * 
 * @example
 * const { isLoading, error, setLoading, setError, setSuccess, reset } = useLoadingState();
 * 
 * // Start loading
 * setLoading(true);
 * 
 * // Handle success
 * setSuccess(data);
 * 
 * // Handle error
 * setError(new Error('Failed to fetch'));
 * 
 * // Reset all states
 * reset();
 */
function useLoadingState(initialLoading = false) {
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [error, setError] = useState(null);

  /**
   * Set loading state
   * Automatically clears error when starting to load
   */
  const setLoading = useCallback((loading) => {
    setIsLoading(loading);
    if (loading) {
      setError(null); // Clear error when starting new operation
    }
  }, []);

  /**
   * Set error state
   * Automatically stops loading
   */
  const setErrorState = useCallback((err) => {
    setError(err);
    setIsLoading(false);
  }, []);

  /**
   * Set success state
   * Automatically stops loading and clears error
   */
  const setSuccess = useCallback(() => {
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * Reset all states to initial values
   */
  const reset = useCallback(() => {
    setIsLoading(initialLoading);
    setError(null);
  }, [initialLoading]);

  /**
   * Execute an async function with automatic loading/error handling
   * 
   * @param {Function} asyncFn - Async function to execute
   * @param {Object} options - Options for execution
   * @param {Function} options.onSuccess - Callback on success
   * @param {Function} options.onError - Callback on error
   * @param {boolean} options.suppressError - Don't set error state (default: false)
   * @returns {Promise} Result of async function
   * 
   * @example
   * const handleFetch = () => execute(
   *   () => fetchProducts(),
   *   {
   *     onSuccess: (data) => setProducts(data),
   *     onError: (err) => console.error(err)
   *   }
   * );
   */
  const execute = useCallback(async (asyncFn, options = {}) => {
    const { onSuccess, onError, suppressError = false } = options;

    setLoading(true);

    try {
      const result = await asyncFn();
      setSuccess();
      
      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      if (!suppressError) {
        setErrorState(err);
      } else {
        setIsLoading(false);
      }

      if (onError) {
        onError(err);
      }

      throw err; // Re-throw so caller can handle if needed
    }
  }, [setLoading, setSuccess, setErrorState]);

  return {
    isLoading,
    error,
    setLoading,
    setError: setErrorState,
    setSuccess,
    reset,
    execute,
    // Computed states for convenience
    hasError: !!error,
    isIdle: !isLoading && !error,
  };
}

export default useLoadingState;
